import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Trophy, ChatCircleText, X, Bookmark, Handshake } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/* v3.2.7 · AttentionFeed — bell icon + dropdown listing recent
   leaderboard events (new #1, verdict changed). Producers write to
   `db.notifications`; this component polls GET /api/notifications
   every 45 s and shows an unread-count badge.

   Only rendered when the user is signed in — PE role sees an empty
   feed until we add PE-facing producers (out of scope for v3.2.7). */
export default function AttentionFeed() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications?limit=20");
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch {
      /* silently ignore — dropdown is opportunistic */
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!user) return;
    const tick = () => {
      // Skip polling when the tab is backgrounded — saves API load.
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      load();
    };
    const t = setInterval(tick, 45000);
    // Also refresh immediately when the tab regains focus.
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user, load]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  if (!user) return null;

  const markRead = async (id) => {
    // Optimistic local update
    setItems((xs) => xs.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((c) => Math.max(0, c - 1));
    try {
      const { data } = await api.post(`/notifications/${id}/read`);
      if (typeof data?.unread_count === "number") setUnread(data.unread_count);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Mark read failed");
      load();
    }
  };

  const markAll = async () => {
    setItems((xs) => xs.map((n) => ({ ...n, read: true })));
    setUnread(0);
    try {
      await api.post("/notifications/read-all");
    } catch (e) {
      toast.error("Bulk mark-read failed");
      load();
    }
  };

  const iconFor = (kind) => {
    if (kind === "leaderboard_top1_change") return <Trophy size={16} weight="fill" className="text-[#FFD700]" />;
    if (kind === "leaderboard_verdict_change") return <ChatCircleText size={16} weight="fill" className="text-[#00E5FF]" />;
    if (kind === "saved_search_match") return <Bookmark size={16} weight="fill" className="text-[#FFCC00]" />;
    if (kind === "milestone_submitted" || kind === "milestone_released"
        || kind === "milestone_rejected" || kind === "proposal_accepted"
        || kind === "deliverable_new"    || kind === "deliverable_update") {
      return <Handshake size={16} weight="fill" className="text-emerald-400" />;
    }
    return <Bell size={16} weight="fill" className="text-gray-400" />;
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 text-gray-300 hover:text-white transition"
        aria-label="Notifications"
        data-testid="attention-feed-toggle"
      >
        <Bell size={20} weight={unread > 0 ? "fill" : "regular"} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-[#FFD700] text-black text-[9px] font-bold flex items-center justify-center rounded-full"
            data-testid="attention-feed-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[380px] max-h-[70vh] overflow-y-auto border border-[#FFD700]/30 bg-[#0F1218] shadow-2xl z-50"
          data-testid="attention-feed-dropdown"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-black/40 sticky top-0">
            <div className="label-tag text-[#FFD700]">// attention feed</div>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button
                  onClick={markAll}
                  className="text-[10px] font-mono text-gray-400 hover:text-white"
                  data-testid="attention-feed-mark-all"
                >
                  mark all read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="text-gray-500 hover:text-white"
                aria-label="Close"
              >
                <X size={12} />
              </button>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-gray-500 font-mono">
              No notifications yet. New leaderboard events land here.
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {items.map((n) => (
                <li
                  key={n.id}
                  className={`px-3 py-3 hover:bg-white/[0.03] ${n.read ? "opacity-60" : ""}`}
                  data-testid={`attention-feed-item-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="pt-0.5">{iconFor(n.kind)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-medium truncate" title={n.title}>
                        {n.title}
                      </div>
                      <div className="text-xs text-gray-300 mt-1 line-clamp-3">{n.body}</div>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[10px] font-mono text-gray-500">
                          {new Date(n.created_at).toLocaleString(undefined, {
                            month: "short", day: "numeric",
                            hour: "2-digit", minute: "2-digit",
                          })}
                        </span>
                        <div className="flex items-center gap-2">
                          {n.kind === "saved_search_match" && n.job_id && (
                            <Link
                              to={`/jobs/${n.job_id}?apply=template`}
                              onClick={() => { markRead(n.id); setOpen(false); }}
                              className="text-[10px] font-mono text-[#FFCC00] hover:underline"
                              data-testid={`attention-feed-apply-${n.id}`}
                            >
                              send my template →
                            </Link>
                          )}
                          {n.job_id && (
                            <Link
                              to={`/jobs/${n.job_id}${
                                // v3.2.18 · milestone deep-link — jump straight to the row.
                                n.milestone_index != null
                                  ? `#milestone-${Number(n.milestone_index) - 1}`
                                  : ""
                              }`}
                              onClick={() => { markRead(n.id); setOpen(false); }}
                              className="text-[10px] font-mono text-[#00E5FF] hover:underline"
                              data-testid={`attention-feed-open-${n.id}`}
                            >
                              {n.milestone_index != null ? `open MS${n.milestone_index} →` : "open job →"}
                            </Link>
                          )}
                          {!n.read && (
                            <button
                              onClick={() => markRead(n.id)}
                              className="text-[10px] font-mono text-gray-400 hover:text-white"
                              data-testid={`attention-feed-read-${n.id}`}
                            >
                              mark read
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
